import { create } from 'zustand';

import { fetchMuxTokens, updateCourseProgress } from '@/api/api';
import type { Course } from '@/api/entitties';

interface MuxTokens {
  playbackToken: string;
  thumbnailToken: string;
}

interface VideoPlayerState {
  // Current selection state
  currentLectureId: string | null;
  currentSectionId: string | null;
  currentCourse: Course | null;

  // Video state
  currentPlaybackId: string | null;
  muxTokens: MuxTokens | null;
  isVideoLoading: boolean;
  shouldAutoplay: boolean;

  // Refs (handled outside store)
  playerRef: React.RefObject<unknown> | null;
  abortController: AbortController | null;
  isPlaying: boolean;

  // Actions
  setCurrentCourse: (course: Course | null) => void;
  setPlayingState: (playing: boolean) => void;
  setPlayerRef: (ref: React.RefObject<unknown>) => void;
  setShouldAutoplay: (autoplay: boolean) => void;
  loadLecture: (courseId: string, sectionId: string, lectureId: string) => Promise<boolean>;
  cleanup: () => void;
  reset: () => void;
}

export const useVideoPlayerStore = create<VideoPlayerState>((set, get) => ({
  // Initial state
  currentLectureId: null,
  currentSectionId: null,
  currentCourse: null,
  currentPlaybackId: null,
  muxTokens: null,
  isVideoLoading: false,
  shouldAutoplay: false,
  playerRef: null,
  abortController: null,
  isPlaying: false,

  // Actions

  setCurrentCourse: (course) => {
    set({ currentCourse: course });
  },

  setPlayingState: (playing) => {
    set({ isPlaying: playing });
  },

  setPlayerRef: (ref) => {
    set({ playerRef: ref });
  },

  setShouldAutoplay: (autoplay) => {
    set({ shouldAutoplay: autoplay });
  },

  loadLecture: async (courseId, sectionId, lectureId) => {
    const state = get();

    if (!state.currentCourse) return false;

    // Ensure course has sections loaded
    if (!state.currentCourse.sections || state.currentCourse.sections.length === 0) {
      console.warn("Cannot load lecture: Course sections not loaded yet");
      return false;
    }

    // If trying to load the same lecture from the same course, do nothing
    if (state.currentLectureId === lectureId &&
        state.currentSectionId === sectionId &&
        state.currentCourse.id === courseId) {
      return true;
    }

    // Abort any previous request
    if (state.abortController) {
      state.abortController.abort();
    }

    // Create a new controller for this request
    const newAbortController = new AbortController();
    const { signal } = newAbortController;

    // Capture the current playing state for autoplay before changing videos
    const wasPlaying = state.isPlaying;

    set({
      abortController: newAbortController,
      shouldAutoplay: wasPlaying,
      isVideoLoading: true,
      currentPlaybackId: null,
      muxTokens: null,
      currentLectureId: lectureId,
      currentSectionId: sectionId,
    });

    // Update course progress asynchronously
    void updateCourseProgress(courseId, { lastSeenLectureId: lectureId }).catch(error => {
      console.error('Failed to update course progress:', error);
    });

    try {
      // Find the lecture in the course
      const section = state.currentCourse.sections?.find(s => s.id === sectionId);
      if (!section) throw new Error("Section not found");

      const lecture = section.lectures.find(l => l.id === lectureId);
      if (!lecture) throw new Error("Lecture not found");

      const tokens = await fetchMuxTokens(courseId, sectionId, lectureId, signal);

      // Update state with new lecture data
      set({
        muxTokens: tokens,
        currentPlaybackId: lecture.asset.playback_ids[0].id,
      });

      return true;
    } catch (error) {
      // Don't handle errors from aborted requests
      if (error instanceof DOMException && error.name === 'AbortError') {
        return false;
      }
      console.error("Failed to load lecture video:", error);

      if ((error instanceof Error ? error.message : String(error)).includes('403')) {
        throw new Error("missing_entitlement");
      }

      throw error;
    } finally {
      // Only update loading state if this request wasn't aborted
      if (!signal.aborted) {
        set({ isVideoLoading: false });
      }
    }
  },

  cleanup: () => {
    const state = get();
    if (state.abortController) {
      state.abortController.abort();
    }
    set({ abortController: null });
  },

  reset: () => {
    const state = get();
    if (state.abortController) {
      state.abortController.abort();
    }
    set({
      currentLectureId: null,
      currentSectionId: null,
      currentCourse: null,
      currentPlaybackId: null,
      muxTokens: null,
      isVideoLoading: false,
      shouldAutoplay: false,
      playerRef: null,
      abortController: null,
      isPlaying: false,
    });
  },
}));

// Computed values (selectors)
export const useCurrentLecture = () => {
  return useVideoPlayerStore((state) => {
    return state.currentCourse
      ?.sections?.find(s => s.id === state.currentSectionId)
      ?.lectures?.find(l => l.id === state.currentLectureId) ?? null;
  });
};
